// Screen Wake Lock helper.
// Available on Android Chrome + iOS Safari 16.4+. Silently no-ops elsewhere.
// Always release on pause/stop — holding a wake lock drains battery.

type WakeLockSentinel = {
  released: boolean;
  release: () => Promise<void>;
  addEventListener: (ev: 'release', cb: () => void) => void;
};

type WakeLockApi = {
  request: (type: 'screen') => Promise<WakeLockSentinel>;
};

function getApi(): WakeLockApi | null {
  const nav = navigator as unknown as { wakeLock?: WakeLockApi };
  return nav.wakeLock ?? null;
}

export function isWakeLockSupported(): boolean {
  return getApi() !== null;
}

export class WakeLockGuard {
  private sentinel: WakeLockSentinel | null = null;
  private reacquireOnVisible = false;

  async acquire(): Promise<boolean> {
    const api = getApi();
    if (!api) return false;
    try {
      this.sentinel = await api.request('screen');
      this.sentinel.addEventListener('release', () => {
        this.sentinel = null;
      });
      return true;
    } catch {
      this.sentinel = null;
      return false;
    }
  }

  async release(): Promise<void> {
    const s = this.sentinel;
    this.sentinel = null;
    this.reacquireOnVisible = false;
    if (s && !s.released) {
      try {
        await s.release();
      } catch {
        // ignore
      }
    }
  }

  /**
   * iOS + Android release the sentinel automatically when the tab hides.
   * Call this on visibilitychange when the user returns so long recordings
   * keep the screen awake without manual toggling.
   */
  armReacquireOnVisible(): void {
    this.reacquireOnVisible = true;
  }

  async handleVisibilityChange(): Promise<void> {
    if (!this.reacquireOnVisible) return;
    if (document.visibilityState === 'visible' && !this.sentinel) {
      await this.acquire();
    }
  }

  get held(): boolean {
    return this.sentinel !== null && !this.sentinel.released;
  }
}
